import { useState, useEffect } from "react";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import DepositFlow from "./DepositFlow";
import WithdrawFlow from "./WithdrawFlow";
import StargatePanel from "./StargateFrame";
import NibiruBridgePanel from "./NibiruBridgePanel";

/**
 * Bridge Modal — users choose a provider:
 *   - Stargate: opens the real stargate.finance app in a new tab, pre-filled
 *     to USDC.e on Nibiru (their site blocks iframing, so we hand off instead
 *     of embedding).
 *   - LayerZero (native flow): our existing Deposit/Withdraw OFT flow, unchanged.
 *   - Nibiru Bridge: opens the official app.nibiru.fi/bridge in a new tab.
 *
 * Props:
 *   open           - boolean
 *   onClose        - callback
 *   walletAddress  - connected EVM wallet address (LayerZero flow only)
 *   initialTab     - "deposit" | "withdraw"
 *   cosmosOnly     - true for Cosmos wallets (Keplr/Leap). The LayerZero flow
 *                    signs through `window.ethereum` (see DepositFlow /
 *                    WithdrawFlow), which a Cosmos wallet cannot provide, so it
 *                    is dropped and only the two hand-off providers are offered
 *                    — those are just external redirects and work for any wallet.
 */
export default function BridgeModal({ open, onClose, walletAddress, initialTab = "deposit", cosmosOnly = false }) {
  const [provider, setProvider] = useState(cosmosOnly ? "nibiru" : "layerzero"); // "stargate" | "layerzero" | "nibiru"
  const [tab, setTab] = useState(initialTab);

  // A Cosmos wallet can never use the LayerZero flow. If the modal is already
  // mounted on it when the wallet switches, move off it rather than leaving a
  // panel up that cannot sign.
  useEffect(() => {
    if (cosmosOnly && provider === "layerzero") setProvider("nibiru");
  }, [cosmosOnly, provider]);

  // Sync tab when initialTab changes (e.g. clicking Deposit vs Withdraw button)
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [initialTab, open]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  if (!open) return null;

  const isStargate = provider === "stargate";
  const isNibiruBridge = provider === "nibiru";
  const isLayerZero = provider === "layerzero";
  const isDeposit = tab === "deposit";

  const panelSize = "max-h-[90dvh] overflow-y-auto rounded-t-[16px] sm:rounded-[16px]";

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[99999] flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className={`w-full sm:w-[420px] max-w-[420px] bg-white shadow-xl overflow-hidden flex flex-col ${panelSize}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Purple gradient bar */}
        <div className="h-[3px] bg-gradient-to-r from-[#5b1dee] via-[#8b5cf6] to-[#5b1dee]" />

        {/* Provider toggle + close */}
        <div className="flex items-center justify-center px-5 pt-4 pb-3 relative">
          <div className="flex bg-[#f3f3f3] rounded-[10px] p-[3px]">
            {!cosmosOnly && (
              <button
                onClick={() => setProvider("layerzero")}
                className={`px-3 py-[6px] rounded-[8px] text-[12px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  provider === "layerzero"
                    ? "bg-gradient-to-b from-[#5b1deec0] to-[#5b1dee] text-white shadow-sm"
                    : "text-[#888] hover:text-[#555]"
                }`}
              >
                LayerZero
              </button>
            )}
            <button
              onClick={() => setProvider("stargate")}
              className={`px-3 py-[6px] rounded-[8px] text-[12px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isStargate
                  ? "bg-gradient-to-b from-[#5b1deec0] to-[#5b1dee] text-white shadow-sm"
                  : "text-[#888] hover:text-[#555]"
              }`}
            >
              Stargate
            </button>
            <button
              onClick={() => setProvider("nibiru")}
              className={`px-3 py-[6px] rounded-[8px] text-[12px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isNibiruBridge
                  ? "bg-gradient-to-b from-[#5b1deec0] to-[#5b1dee] text-white shadow-sm"
                  : "text-[#888] hover:text-[#555]"
              }`}
            >
              Nibiru Bridge
            </button>
          </div>
          <button
            onClick={onClose}
            className="absolute right-5 p-1.5 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Deposit/withdraw sub-toggle — only LayerZero has a direction to pick; Stargate and Nibiru Bridge are single redirects */}
        {isLayerZero && (
          <div className="flex items-center justify-center px-5 pb-3">
            <div className="flex bg-[#f3f3f3] rounded-[10px] p-[3px]">
              <button
                onClick={() => setTab("deposit")}
                className={`flex items-center gap-1.5 px-4 py-[6px] rounded-[8px] text-[13px] font-semibold transition-all cursor-pointer ${
                  isDeposit
                    ? "bg-gradient-to-b from-[#5b1deec0] to-[#5b1dee] text-white shadow-sm"
                    : "text-[#888] hover:text-[#555]"
                }`}
              >
                <ArrowDown size={14} />
                Deposit
              </button>
              <button
                onClick={() => setTab("withdraw")}
                className={`flex items-center gap-1.5 px-4 py-[6px] rounded-[8px] text-[13px] font-semibold transition-all cursor-pointer ${
                  !isDeposit
                    ? "bg-gradient-to-b from-[#5b1deec0] to-[#5b1dee] text-white shadow-sm"
                    : "text-[#888] hover:text-[#555]"
                }`}
              >
                <ArrowUp size={14} />
                Withdraw
              </button>
            </div>
          </div>
        )}

        {isStargate ? (
          <>
            <StargatePanel />
            <div className="text-center py-2 border-t border-gray-100">
              <a
                href="https://stargate.finance/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] text-[#aaa] hover:text-[#888] transition-colors"
              >
                Powered by{" "}
                <span className="text-[#5b1dee] font-medium">Stargate</span>
              </a>
            </div>
          </>
        ) : isNibiruBridge ? (
          <NibiruBridgePanel />
        ) : (
          <>
            {/* Content */}
            <div className="px-5 pb-5">
              {isDeposit ? (
                <DepositFlow walletAddress={walletAddress} />
              ) : (
                <WithdrawFlow walletAddress={walletAddress} />
              )}
            </div>

            {/* Footer */}
            <div className="text-center pb-4">
              <a
                href="https://layerzero.network/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] text-[#aaa] hover:text-[#888] transition-colors"
              >
                Powered by{" "}
                <span className="text-[#5b1dee] font-medium">LayerZero</span>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
